/**
 * Font Awesome (ADR-131) : kit chargé par le navigateur quand `VITE_FONTAWESOME_KIT` est posé à la construction.
 * Les icônes s'insèrent **dans** la balise `<i>` (`autoReplaceSvg: 'nest'`) au lieu de la remplacer : React garde
 * la main sur ses éléments. Sans kit (tests, démo hors ligne), le registre SVG du design system sert de repli.
 */
const KIT = (import.meta.env['VITE_FONTAWESOME_KIT'] as string | undefined)?.trim() ?? '';

/** Style par défaut des icônes : `regular`, proche du trait des icônes dessinées jusqu'ici. */
export const FONT_AWESOME_STYLE = 'regular';

export const fontAwesomeEnabled = (): boolean => KIT !== '' && /^[a-z0-9]{6,20}$/i.test(KIT);

/** Charge le kit une seule fois ; sans identifiant valide, ne fait rien. */
export function loadFontAwesomeKit(): void {
  if (!fontAwesomeEnabled() || typeof document === 'undefined' || document.getElementById('ay-fontawesome-kit') !== null) return;
  (window as unknown as { FontAwesomeConfig?: Record<string, unknown> }).FontAwesomeConfig = { autoReplaceSvg: 'nest', observeMutations: true };
  const script = document.createElement('script');
  script.id = 'ay-fontawesome-kit';
  script.src = `https://kit.fontawesome.com/${KIT}.js`;
  script.crossOrigin = 'anonymous';
  script.async = true;
  document.head.appendChild(script);
}

/** Nom du registre du design system → icône Font Awesome (les noms de la navigation ne changent pas). */
export const FONT_AWESOME_NAMES: Readonly<Record<string, string>> = {
  folder: 'folder',
  image: 'image',
  upload: 'upload',
  lock: 'lock',
  mail: 'envelope',
  phone: 'phone',
  mobile: 'mobile-screen-button',
  location: 'location-dot',
  clock: 'clock',
  thermometer: 'temperature-half',
  droplet: 'droplet',
  gauge: 'gauge',
  home: 'house',
  inbox: 'inbox',
  package: 'box',
  clipboard: 'clipboard-check',
  layers: 'layer-group',
  users: 'users',
  user: 'user',
  receipt: 'receipt',
  settings: 'gear',
  building: 'building',
  chart: 'chart-line',
  logout: 'right-from-bracket',
  shield: 'shield-halved',
  file: 'file',
  book: 'book',
  help: 'circle-question',
  info: 'circle-info',
  truck: 'truck',
  scale: 'scale-balanced',
  alert: 'triangle-exclamation',
  search: 'magnifying-glass',
  chevron: 'chevron-down',
  'chevron-down': 'chevron-down',
  'chevron-up': 'chevron-up',
  menu: 'bars',
  close: 'xmark',
  calendar: 'calendar',
  tag: 'tag',
  euro: 'euro-sign',
  database: 'database',
  dot: 'circle',
  video: 'video',
  sparkle: 'wand-magic-sparkles',
  check: 'check',
  more: 'ellipsis',
  pencil: 'pen',
  move: 'arrows-up-down-left-right',
  archive: 'box-archive',
  trash: 'trash-can',
  refresh: 'rotate',
  download: 'download',
  filter: 'filter',
  group: 'object-group',
  sort: 'arrow-down-wide-short',
  table: 'table',
  cards: 'grip',
  back: 'arrow-left',
  columns: 'table-columns',
  grid: 'table-cells-large',
  rows: 'list',
  plus: 'plus',
  eye: 'eye',
  // Explorateur du graphe et atlas du modèle (ADR-129, ADR-130).
  graph: 'diagram-project',
  minus: 'minus',
  fit: 'expand',
  maximize: 'up-right-and-down-left-from-center',
  minimize: 'down-left-and-up-right-to-center',
  // Éditeurs des studios (ADR-146 addendum (12)).
  undo: 'arrow-rotate-left',
  redo: 'arrow-rotate-right',
  history: 'clock-rotate-left',
  tablet: 'tablet-screen-button',
  monitor: 'desktop',
  rotate: 'rotate',
  copy: 'copy',
  'arrow-down': 'arrow-down',
  'corner-up': 'arrow-turn-up',
  play: 'play',
  // Icônes de type déclarées par un Pack (ADR-129, object-definition 1.9.0) : noms génériques du registre.
  drumstick: 'drumstick-bite',
  boxes: 'boxes-stacked',
  'truck-fast': 'truck-fast',
  dolly: 'dolly',
  stethoscope: 'stethoscope',
  industry: 'industry',
  'person-rifle': 'person-rifle',
  // Natures des champs (addendum (15)) et éditeur v2 des formulaires (addendum (18)).
  'eye-off': 'eye-slash',
  text: 'font',
  'text-long': 'align-left',
  hash: 'hashtag',
  stepper: 'plus-minus',
  slider: 'sliders',
  star: 'star',
  toggle: 'toggle-on',
  tiles: 'grid-2',
  segmented: 'table-columns',
  list: 'list',
  scan: 'barcode-read',
  camera: 'camera',
  signature: 'signature',
  link: 'link',
  // Formulaires 1.10.0 (ADR-146 addendum (16), point 8).
  paw: 'paw',
  'arrow-right': 'arrow-right',
  circle: 'circle',
  pointer: 'hand-pointer',
  chip: 'tag',
  sigma: 'sigma',
  'id-card': 'id-card',
  translate: 'language',
  grip: 'grip-vertical',
  'arrow-left': 'arrow-left',
  unlink: 'link-slash',
  layout: 'table-layout',
};
